// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { features } from '@/lib/edition';
import { registerLogoutHook } from '@/lib/logoutHooks';
import type { NoteData } from '@/service/notesApi';
import { useNoteStore } from '@/store/noteStore';
import { create } from 'zustand';

/** The composer of a conversation that has no project yet. */
export const DRAFT_KEY = '__draft__';

export type RequestNote = { name: string; content: string };

interface NoteAttachState {
  /** Notes attached to the next turn, per conversation (project id). */
  attached: Record<string, NoteData[]>;
  get: (key: string) => NoteData[];
  toggle: (key: string, note: NoteData) => void;
  remove: (key: string, noteId: number) => void;
  clear: (key: string) => void;
  reset: () => void;
}

export const useNoteAttachStore = create<NoteAttachState>((set, get) => ({
  attached: {},

  get: (key) => get().attached[key] ?? [],

  toggle: (key, note) =>
    set((state) => {
      const current = state.attached[key] ?? [];
      const next = current.some((n) => n.id === note.id)
        ? current.filter((n) => n.id !== note.id)
        : [...current, note];
      return { attached: { ...state.attached, [key]: next } };
    }),

  remove: (key, noteId) =>
    set((state) => ({
      attached: {
        ...state.attached,
        [key]: (state.attached[key] ?? []).filter((n) => n.id !== noteId),
      },
    })),

  clear: (key) =>
    set((state) => ({ attached: { ...state.attached, [key]: [] } })),

  reset: () => set({ attached: {} }),
}));

/** Notes handed to a specific task by the user's send, until its request is built. */
const staged = new Map<string, RequestNote[]>();

function slotFor(projectId: string | null | undefined): string {
  // The picker keys a conversation by its project id, and the draft composer
  // (no project yet) by DRAFT_KEY; the send resolves the same way.
  return projectId || DRAFT_KEY;
}

/**
 * What goes into a chat request: the notes attached to this conversation, as
 * name + content only. Taking them clears them, so they never reach the next
 * turn. The community edition sends nothing.
 */
export function takeNotesForRequest(
  projectId: string | null | undefined
): RequestNote[] {
  if (!features.notes) return [];
  const key = slotFor(projectId);
  const list = useNoteAttachStore.getState().attached[key] ?? [];
  if (list.length === 0) return [];
  useNoteAttachStore.getState().clear(key);
  return list.map((n) => ({ name: n.name, content: n.content }));
}

/**
 * The user's send takes the composer's notes and stages them for the task it
 * is about to start. Only that task's request can take them back, so a
 * trigger, a Remote Control command or a resume that also goes through
 * startTask never sends what the user attached for something else.
 */
export function stageNotesForTask(
  taskId: string,
  projectId: string | null | undefined
): void {
  const notes = takeNotesForRequest(projectId);
  if (notes.length > 0) staged.set(taskId, notes);
}

export function takeStagedNotes(taskId: string): RequestNote[] {
  if (!features.notes) return [];
  const notes = staged.get(taskId) ?? [];
  staged.delete(taskId);
  return notes;
}

// Keep attachments consistent with the notes list: a note that was really
// deleted stops being sent, an edited note is sent in its new form. A note
// that merely is not in the (filtered) list is left alone.
useNoteStore.subscribe((state, previous) => {
  if (state.removed && state.removed !== previous.removed) {
    const id = state.removed.id;
    useNoteAttachStore.setState((s) => ({
      attached: Object.fromEntries(
        Object.entries(s.attached).map(([key, list]) => [
          key,
          list.filter((n) => n.id !== id),
        ])
      ),
    }));
  }
  if (state.notes !== previous.notes) {
    const current = new Map(state.notes.map((n) => [n.id, n]));
    useNoteAttachStore.setState((s) => {
      let changed = false;
      const attached: Record<string, NoteData[]> = {};
      for (const [key, list] of Object.entries(s.attached)) {
        attached[key] = list.map((n) => {
          const fresh = current.get(n.id);
          if (fresh && fresh !== n) {
            changed = true;
            return fresh;
          }
          return n;
        });
      }
      return changed ? { attached } : s;
    });
  }
});

registerLogoutHook(() => {
  useNoteAttachStore.getState().reset();
  staged.clear();
});
